import { cn } from "@/lib/utils";

/**
 * Shared styling for the app's left navs — the grain **"dark band rail"** design
 * (decided 2026-07-12; supersedes the earlier light-surface treatment). Used by
 * `NavMain` (chat shell), `DashboardsNav` (/dashboards workspace) and
 * `SettingsNav`, which previously each duplicated these constants with
 * "mirrors DashboardsNav" comments — the fork ends here.
 *
 * The iron rule (grain skill): components consume tokens, never own colors. The
 * rail paints `--rail-surface` and every on-band tint is expressed as
 * `color-mix(in srgb, var(--rail-ink) N%, transparent)` — never a raw
 * white/black/hex literal. Token *values* live only in `dashboard-themes.css`.
 *
 * The rail quartet is the theme-INVARIANT set (`--rail-surface` / `-ink` /
 * `-active` / `-active-ink`), defined once on `[data-dash-theme]` and never
 * overridden in a `.dark` block — so, unlike the swapping `--dash-band*`
 * quartet, the rail reads identically in light and dark while the breadcrumb
 * strip and content area keep tracking the theme (F20).
 *
 * Every token carries a base-semantic fallback for the rare mount outside a
 * themed root: `--grey-900` (surface) pairs with `--grey-100` (ink) — they swap
 * together, so the pair stays contrasting in both. Dark mode therefore needs
 * zero `theme === "dark"` branching for color; the band is dark in both modes.
 */

// ── Spacing (north-star `.nav` rhythm) ───────────────────────────────────────

/** Rail body padding — north-star `.nav`: 18px top, 14px sides, 16px bottom
 *  (replaces the legacy uniform `p-3`). Applied on the `.iq-sidebar-container`. */
export const NAV_RAIL_PADDING = "px-[14px] pt-[18px] pb-4";

/** Gap below the brand block and between top-level sections (~18px). */
export const NAV_SECTION_GAP = "mb-[18px]";

/** Gap below the primary action button (New chat/dashboard) — 16px, matching the
 *  approved mock's `.rl-new` `margin:0 0 16px`. */
export const NAV_PRIMARY_GAP = "mb-4";

/** Gap below the ghost back-button (Back to chat / Go back) — 12px, matching the
 *  north-star `.nav-btn-back` margin. Shared by DashboardsNav/SettingsNav/AdminNav
 *  so the restated `mb-3` literals don't drift apart. */
export const NAV_BACK_GAP = "mb-3";

/** Tight in-section item stack gap (north-star ~1px; 2px reads the same and
 *  keeps focus rings from touching). Pair on a `SidebarMenu`. */
export const NAV_ITEM_STACK_GAP = "gap-[2px]";

/** Gap between DESTINATION pill rows (My Requests · Reports · Dashboards · Chats)
 *  — 4px, looser than the 2px recents stack so the destination cluster reads as
 *  the primary wayfinding band, denser recents below it (the approved mock's
 *  hierarchy: destinations breathe, recents pack). Pair on the destinations
 *  `SidebarMenu`. */
export const NAV_DESTINATION_STACK_GAP = "gap-1";

/** Vertical separation between top-level rail groups (destinations, Recent
 *  dashboards/drafts/chats, Use cases) — 16px (the zone-boundary token). Tightened
 *  from the earlier 24px per the approved flow/nav audit (Q1 verdict): the group
 *  labels still get clear air, but the zones read as one continuous rail rather
 *  than drifting apart. One token so every zone gap tightens consistently. */
export const NAV_GROUP_GAP = "mb-4";

// ── Rail surfaces ────────────────────────────────────────────────────────────

/** The expanded rail body: band surface + ink. `--rail-edge` draws the right
 *  boundary — transparent on dark-band themes (contrast is the separation),
 *  a visible border on light-rail themes like classic (prod parity). */
export const NAV_RAIL_CONTAINER_CLASS =
  "bg-[var(--rail-surface,var(--grey-900))] text-[color:var(--rail-ink,var(--grey-100))] border-r border-[var(--rail-edge,transparent)]";

/** The collapsed desktop rail (`w-14` aside): band surface + the same edge. */
export const NAV_COLLAPSED_RAIL_CLASS =
  "bg-[var(--rail-surface,var(--grey-900))] border-r border-[var(--rail-edge,transparent)]";

/** Collapsed desktop rail width choreography — always mounted, width animates
 *  0↔56px in sync with the offcanvas slide (same 200ms + --dash-ease) so the
 *  content edge moves monotonically in one motion instead of popping in. */
export const navCollapsedRailClass = (showSidebar: boolean) =>
  cn(
    "hidden md:flex h-screen shrink-0 flex-col items-center justify-between overflow-hidden py-3",
    "transition-[width] duration-200 ease-[var(--dash-ease)] motion-reduce:transition-none",
    showSidebar ? "w-0" : "w-14",
    NAV_COLLAPSED_RAIL_CLASS,
  );

/** Brand wordmark ("iQ") ink — reads on the band. The SarasIQ mark + star stay
 *  in accent tones at the call site. */
export const NAV_BRAND_TEXT_CLASS = "text-[color:var(--rail-ink,var(--grey-100))]";

/** Brand header row — the approved mock's `.rl-brand`: brand cluster left,
 *  collapse toggle right, with the mock's `padding:2px 4px 14px` so the row
 *  breathes off the viewport edge instead of hugging it. */
export const NAV_BRAND_HEADER_CLASS =
  "flex h-auto w-full items-center justify-between px-1 pt-[2px] pb-5";

// ── Buttons ──────────────────────────────────────────────────────────────────

/** Primary action (New chat / New dashboard): solid accent, on-accent ink,
 *  9px radius (never rounded-full), 12.5px/650, hover brightness, press scale. */
export const NAV_BUTTON_PRIMARY_CLASS = cn(
  "flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-[9px] border-0 shadow-[0_1px_2px_rgba(0,0,0,0.2)]",
  // `--rail-cta(-ink)` lets a theme give the CTA its own fill when the
  // active-row tint isn't a button color (classic light: mint rows, solid
  // #00a97f CTA per prod). Falls back to the rail-active pair everywhere else.
  "bg-[var(--rail-cta,var(--rail-active,var(--primary-500)))] text-[12.5px] font-[650] text-[color:var(--rail-cta-ink,var(--rail-active-ink,var(--structural-white)))]",
  "transition-[transform,filter] duration-150 ease-[var(--dash-ease)]",
  // Keep the accent bg on hover (override the default Button variant's
  // hover:bg-primary/90) and lift it with brightness instead.
  "hover:bg-[var(--rail-cta,var(--rail-active,var(--primary-500)))] hover:brightness-[1.08] active:scale-[.97]",
  "motion-reduce:transition-none motion-reduce:active:scale-100",
);

/** Ghost rail button (Back to chat / Go back): transparent, band-ink 68% mix
 *  text; hover band-ink 6% mix bg + 8% mix border. */
export const NAV_BUTTON_GHOST_CLASS = cn(
  "flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-[9px] border border-transparent bg-transparent shadow-none",
  "text-[12.5px] font-semibold text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_68%,transparent)]",
  "transition-[transform,background-color,border-color,color] duration-150 ease-[var(--dash-ease)]",
  "hover:bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)] hover:border-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_8%,transparent)] hover:text-[color:var(--rail-ink,var(--grey-100))]",
  "active:scale-[.97] motion-reduce:transition-none motion-reduce:active:scale-100",
);

/** Square icon-only ghost button (collapse toggle, collapsed-rail actions).
 *  Consumers add the size (`size-8` / `size-9`). Focus ring inset like the nav
 *  rows — these sit flush against overflow-clipped headers that shear an
 *  outward ring. */
export const NAV_ICON_GHOST_BTN_CLASS = cn(
  "cursor-pointer rounded-[7px] bg-transparent p-0 shadow-none focus-visible:ring-inset",
  "transition-[transform,background-color] duration-150 ease-[var(--dash-ease)]",
  "hover:bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)]",
  "active:scale-[.97] motion-reduce:transition-none motion-reduce:active:scale-100",
);

/** Square icon-only CTA button — the collapsed-rail twin of
 *  {@link NAV_BUTTON_PRIMARY_CLASS}. New Chat is an *action*, not a destination,
 *  so it never takes the active tint; it carries the accent fill instead, which
 *  is what tells it apart from the destination icons stacked beneath it (QA read
 *  the old ghost treatment as "a nav icon whose active state is missing").
 *  Radius/press feedback match the ghost icon buttons so the column stays one
 *  family. Consumers add the size (`size-9`). */
export const NAV_ICON_CTA_BTN_CLASS = cn(
  "cursor-pointer rounded-[7px] border-0 p-0 shadow-[0_1px_2px_rgba(0,0,0,0.2)] focus-visible:ring-inset",
  "bg-[var(--rail-cta,var(--rail-active,var(--primary-500)))]",
  "transition-[transform,filter] duration-150 ease-[var(--dash-ease)]",
  "hover:bg-[var(--rail-cta,var(--rail-active,var(--primary-500)))] hover:brightness-[1.08] active:scale-[.97]",
  "motion-reduce:transition-none motion-reduce:active:scale-100",
);

/** Icon color that pairs with {@link NAV_ICON_CTA_BTN_CLASS}'s accent fill. */
export const NAV_ICON_CTA_COLOR =
  "text-[color:var(--rail-cta-ink,var(--rail-active-ink,var(--structural-white)))]";

/** Icon color for an inactive icon-ghost button (band-ink 68% mix). */
export const NAV_ICON_GHOST_COLOR =
  "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_68%,transparent)]";

/** Active-item tint for the collapsed rail (band-active 22% mix, or the theme's
 *  own `--rail-active-row` — see {@link navItemClass}). */
export const NAV_COLLAPSED_ACTIVE_BG =
  "bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))]";

/** Collapsed-rail icon color, active vs inactive. `--rail-active-icon` lets a
 *  theme whose `--rail-active` is a row TINT rather than an accent (classic, in
 *  both modes) paint the icon with the row ink instead — without it the active
 *  icon all but vanishes. Themes with a real accent omit the token and fall
 *  through to `--rail-active` exactly as before. */
export const navCollapsedIconClass = (isActive: boolean) =>
  cn(
    isActive
      ? "text-[color:var(--rail-active-icon,var(--rail-active,var(--primary-500)))]"
      : "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_68%,transparent)]",
  );

// ── Section headers ──────────────────────────────────────────────────────────

/** Uppercase eyebrow for section labels — band-ink 45% mix. */
export const NAV_LABEL_CLASS =
  "text-[10.5px] font-semibold uppercase tracking-[.13em] text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_45%,transparent)]";

/** Group-hover brightening for the eyebrow (band-ink 65% mix) — pair with a
 *  `group/section` parent. */
export const NAV_LABEL_HOVER_CLASS =
  "group-hover/section:text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_65%,transparent)]";

/** Section item count — 10.5px tabular, band-ink 38% mix. */
export const NAV_COUNT_CLASS =
  "text-[10.5px] leading-none tabular-nums text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_38%,transparent)]";

/** Section chevron color — band-ink 42% mix. Consumers add size + rotation. */
export const NAV_CHEVRON_COLOR =
  "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_42%,transparent)]";

/** Hover background for a section-header button (band-ink 6% mix). */
export const NAV_SECTION_HEAD_HOVER =
  "hover:bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)]";

// ── Rows ─────────────────────────────────────────────────────────────────────

/**
 * Uniform interactive-row geometry shared by EVERY rail list — chat threads,
 * dashboards, drafts, manage, settings, admin — so no list reads taller or
 * looser than another (F18): one height (32px), gap, side-padding and text size.
 * `navItemClass` rows are `SidebarMenuButton`s that already resolve to this spec
 * (variant `h-8`, base `gap-2`, `pl-4` at the call site, `text-[12.5px]`); the
 * chat thread rows — a nested `SidebarMenuSubButton` that otherwise defaulted to
 * `h-7`/`gap-3` and read looser — consume this constant directly to match.
 */
export const NAV_ROW_GEOMETRY = "h-8 gap-2 pl-4 pr-2 text-[12.5px]";

/**
 * A nav list row. Radius 7, 12.5px, band-ink 78% mix text; leading icon
 * inherits. Hover: band-ink 6% mix bg + full band-ink. Active: band-active 22%
 * mix bg + full band-ink, weight 640. Press scale .97; focus rings stay INSET
 * (the scroll wrappers shear outward rings).
 *
 * `--rail-active-row` is the same escape hatch as `--rail-active-icon`: a theme
 * whose `--rail-active` is a row TINT rather than an accent (classic, in both
 * modes) defines it so the row paints at FULL strength — 22% of an already
 * near-surface tint reads as no active state at all. Themes with a real accent
 * omit the token and keep the 22% mix exactly as before.
 */
export const navItemClass = (isActive: boolean) =>
  cn(
    "cursor-pointer rounded-[7px] px-2 py-[7px] text-[12.5px] font-medium",
    "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_78%,transparent)]",
    "transition-[transform,background-color,color] duration-150 ease-[var(--dash-ease)] motion-reduce:transition-none",
    "hover:bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)] hover:text-[color:var(--rail-ink,var(--grey-100))]",
    "active:scale-[.97] motion-reduce:active:scale-100",
    "focus-visible:ring-inset",
    isActive &&
      "font-[640] bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] text-[color:var(--rail-ink,var(--grey-100))] hover:bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] hover:text-[color:var(--rail-ink,var(--grey-100))] data-[active=true]:bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] data-[active=true]:text-[color:var(--rail-ink,var(--grey-100))]",
  );

/**
 * A DESTINATION pill row — the primary wayfinding cluster (My Requests · Reports
 * · Dashboards · Chats). Taller and roomier than the compact `navItemClass`
 * recents rows (h-10 / 40px vs ~32px, 13px text, 8px radius) so the two densities
 * carry the hierarchy the approved mock encodes (Fix 1 / F26): destinations are
 * tap targets you return to; recents are a dense scannable slice. Same band-ink
 * tint grammar and active treatment as `navItemClass`.
 */
export const navDestinationClass = (isActive: boolean) =>
  cn(
    "h-10 cursor-pointer rounded-[8px] px-2.5 text-[13px] font-medium",
    "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_78%,transparent)]",
    "transition-[transform,background-color,color] duration-150 ease-[var(--dash-ease)] motion-reduce:transition-none",
    "hover:bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)] hover:text-[color:var(--rail-ink,var(--grey-100))]",
    "active:scale-[.97] motion-reduce:active:scale-100",
    "focus-visible:ring-inset",
    isActive &&
      "font-[640] bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] text-[color:var(--rail-ink,var(--grey-100))] hover:bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] hover:text-[color:var(--rail-ink,var(--grey-100))] data-[active=true]:bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))] data-[active=true]:text-[color:var(--rail-ink,var(--grey-100))]",
  );

/** Row leading icon. The colors are `!important`: shadcn's SidebarMenuSubButton
 *  styles direct svg children via `[&>svg]:text-sidebar-…` (class+element
 *  specificity), which beats a plain utility on the svg itself and left sub-row
 *  icons illegible on the band. Active uses `--rail-active-icon` first — see
 *  {@link navCollapsedIconClass} for why the tint themes need the extra hop. */
export const navItemIconClass = (isActive: boolean) =>
  cn(
    "size-4 shrink-0",
    isActive
      ? "!text-[color:var(--rail-active-icon,var(--rail-active,var(--primary-500)))]"
      : "!text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_78%,transparent)]",
  );

/** Row label — inherits the row ink (hover/active handled by the row). The
 *  `isActive` arg is accepted for call-site symmetry with the icon/row helpers. */
export const navItemLabelClass = (isActive?: boolean) => cn("truncate", isActive && "");

/** Muted trailing/leading icon on a row (e.g. the options `⋮`) — band-ink 55% mix. */
export const NAV_MUTED_ICON_CLASS =
  "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_55%,transparent)]";

/** Faint on-band body text (empty-state copy) — band-ink 45% mix. */
export const NAV_FAINT_TEXT_CLASS =
  "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_45%,transparent)]";

// ── Chat sub-rows (NavMain thread list) ──────────────────────────────────────

/** Hover/open background for a chat sub-row (band-ink 6% mix). */
export const NAV_SUBROW_HOVER_BG =
  "bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_6%,transparent)]";

/** Active background for a chat sub-row (band-active 22% mix, or the theme's own
 *  `--rail-active-row` — see {@link navItemClass}). */
export const NAV_SUBROW_ACTIVE_BG =
  "bg-[var(--rail-active-row,color-mix(in_srgb,var(--rail-active,var(--primary-500))_22%,transparent))]";

// ── Footer, badges, skeletons, dividers ──────────────────────────────────────

/** Footer top rule — band-ink 8% mix border. */
export const NAV_FOOTER_RULE_CLASS =
  "border-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_8%,transparent)]";

/** A thin divider inside the collapsed rail — band-ink 12% mix. */
export const NAV_DIVIDER_CLASS =
  "bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_12%,transparent)]";

/** Count/notification badge — accent bg + on-accent ink. */
export const NAV_BADGE_CLASS =
  "rounded-full bg-[var(--rail-active,var(--primary-500))] text-[10px] text-[color:var(--rail-active-ink,var(--structural-white))]";

/** Skeleton loader block on the band — band-ink 10% mix bg (dark in both modes). */
export const NAV_SKELETON_CLASS =
  "bg-[color-mix(in_srgb,var(--rail-ink,var(--grey-100))_10%,transparent)]";

// ── Mobile top bars (topbars, NOT the rail — kept light, tokenized) ───────────

/** Mobile `lg:hidden` top bar surface. */
export const NAV_MOBILE_BAR_CLASS = "bg-[var(--dash-surface,var(--structural-white))]";

/** Mobile top-bar icon color. */
export const NAV_MOBILE_ICON_CLASS = "text-[color:var(--dash-muted,var(--text-medium-emphasis))]";
